/**
 * Every hardcoded coach→team grant behind Apollo's workout-builder gates, so a
 * roster change is one edit in one file rather than three the next person has
 * to remember exist. Chat impersonation keeps its own lists —
 * `ui/components/chat/impersonation-controller.tsx` and the deny-list in
 * `queries/teams/hooks.ts` — and is deliberately not folded in here.
 *
 * Rocky no longer scopes workout writes by team-coach membership, so these maps
 * are the only thing deciding which extra teams a coach may touch in Apollo.
 * They gate the UI, not the API — anything granted here is a product decision,
 * not a security boundary.
 */

const ELISE_YOUNG = "612b34d6-8e49-4b0c-a5ed-ab79c8101169";
const LAUREN = "6720ae04-51ef-46ba-ab7e-2f9675fc0493";
const SAMANTHA = "614035e3-1d63-44a2-95bb-ae63fab9d63e";
const NAIREE_KIANA = "6d7b7376-5e11-40df-b81c-c42c02600cc8";

const DEFINE = "3a3ee0d8-e817-4c06-8620-351a27da67f1";
const MOVEWELL = "433adeaf-734a-4942-af48-a59e56970f89";
const IGNITE = "7d8d84cc-bebe-45cf-a932-cbb4cd89f4a5";
const PRENATAL = "31bf0334-b750-42fe-981d-5a95b8256f78";

/** Full programming rights: edit, clone, delete, create weeks. */
const PROGRAMMING_GRANTS: Record<string, string[]> = {
  // Assisting Maia with Define's programming (LADD-4756)
  [ELISE_YOUNG]: [DEFINE],
};

/**
 * Week export only. Read-only — it re-fetches each workout and builds the
 * text/CSV client-side — so it is granted more widely than editing.
 */
const WEEK_EXPORT_GRANTS: Record<string, string[]> = {
  [ELISE_YOUNG]: [DEFINE, MOVEWELL, IGNITE],
};

/**
 * Teams whose coach list is pinned here instead of read from the Rocky roster.
 * Membership is a *replacement*, not an addition: on one of these teams a coach
 * outside the list does not count as a team coach even if Rocky rosters them,
 * which is why callers branch on `hasPinnedRoster` before falling through to
 * the roster check.
 */
const PINNED_ROSTERS: Record<string, string[]> = {
  [PRENATAL]: [SAMANTHA, ELISE_YOUNG, LAUREN],
};

/**
 * Cloning a workout carries its voiceovers across, so the audio lands under a
 * coach who never recorded it. Granted per coach, not per team — the team side
 * is already gated by `canProgramTeam` at the call site.
 */
const VOICEOVER_CLONE_GRANTS: string[] = [
  ELISE_YOUNG,
  LAUREN,
  SAMANTHA,
  NAIREE_KIANA,
];

const isGranted = (
  grants: Record<string, string[]>,
  userID?: string,
  teamID?: string
): boolean => {
  if (!userID || !teamID) return false;
  const teams = grants[userID];
  return Array.isArray(teams) && teams.includes(teamID);
};

export const hasProgrammingGrant = (userID?: string, teamID?: string): boolean =>
  isGranted(PROGRAMMING_GRANTS, userID, teamID);

export const hasWeekExportGrant = (userID?: string, teamID?: string): boolean =>
  isGranted(WEEK_EXPORT_GRANTS, userID, teamID);

export const hasPinnedRoster = (teamID?: string): boolean =>
  !!teamID && Array.isArray(PINNED_ROSTERS[teamID]);

export const isPinnedRosterCoach = (
  userID?: string,
  teamID?: string
): boolean => {
  if (!userID || !teamID) return false;
  const roster = PINNED_ROSTERS[teamID];
  return Array.isArray(roster) && roster.includes(userID);
};

export const hasVoiceoverCloneGrant = (userID?: string): boolean =>
  !!userID && VOICEOVER_CLONE_GRANTS.includes(userID);

/**
 * Every team `canProgramTeam` would pass for this coach, given the teams Rocky
 * rosters them on: pinned rosters override the Rocky roster, and programming
 * grants add teams on top.
 */
export const programmableTeamIDs = (
  userID: string | undefined,
  rosterTeamIDs: string[]
): string[] => {
  if (!userID) return [];
  const ids = rosterTeamIDs.filter(
    (teamID) => !hasPinnedRoster(teamID) || isPinnedRosterCoach(userID, teamID)
  );
  for (const [teamID, roster] of Object.entries(PINNED_ROSTERS)) {
    if (roster.includes(userID)) ids.push(teamID);
  }
  const granted = PROGRAMMING_GRANTS[userID];
  if (Array.isArray(granted)) ids.push(...granted);
  return [...new Set(ids)];
};
